import React, { useState, useRef } from 'react';
import api from '../services/api';
import { Upload, Search, Shield, AlertTriangle, CheckCircle, XCircle, FileText, Download } from 'lucide-react';

interface ScreeningResult {
  dotNumber: number;
  legalName: string;
  state: string;
  powerUnits: number;
  safetyRating: string;
  activeOOS: boolean;
  oosRate: number;
  crashCount: number;
  fatalCrashes: number;
  violationCount: number;
  insuranceStatus: string;
  lapseRisk: string;
  relatedCarriers: number;
  riskFlags: string[];
  recommendation: string;
  riskScore: number;
}

const BulkScreeningPage: React.FC = () => {
  const [dotInput, setDotInput] = useState('');
  const [results, setResults] = useState<ScreeningResult[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const parseDots = (text: string): number[] => {
    return text.split(/[\n,;\s]+/).map(s => parseInt(s.trim())).filter(n => !isNaN(n) && n > 0);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      // Extract numbers from CSV — handles DOT columns, headers, etc.
      const nums = text.split(/[\n,;\s\r]+/).map(s => parseInt(s.replace(/[^0-9]/g, ''))).filter(n => !isNaN(n) && n > 1000);
      setDotInput(nums.join('\n'));
    };
    reader.readAsText(file);
  };

  const screen = async () => {
    const dots = parseDots(dotInput);
    if (dots.length < 1) { setError('Enter at least 1 DOT number'); return; }
    if (dots.length > 200) { setError('Maximum 200 carriers per screening'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.post('/carriers/screen', { dotNumbers: dots });
      // The axios response interceptor in services/api.ts already unwraps the
      // { success, data } envelope, so res.data IS the payload.
      setResults(res.data.results);
      setSummary(res.data.summary);
    } catch (e: any) { setError(e.response?.data?.error || 'Screening failed'); }
    setLoading(false);
  };

  const exportCSV = () => {
    if (results.length === 0) return;
    const headers = 'DOT,Legal Name,State,Fleet Size,Safety Rating,OOS Rate,Crashes,Fatal,Violations,Insurance,Recommendation,Risk Score,Flags\n';
    const rows = results.map(r =>
      `${r.dotNumber},"${r.legalName}",${r.state},${r.powerUnits},${r.safetyRating},${r.oosRate}%,${r.crashCount},${r.fatalCrashes},${r.violationCount},${r.insuranceStatus},${r.recommendation},${r.riskScore},"${r.riskFlags.join('; ')}"`
    ).join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `tsap-screening-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const recColor = (rec: string) => {
    switch (rec) {
      case 'APPROVE': return { bg: '#e2e7d5', color: '#4b5c3a', border: '#d1e6d9' };
      case 'REVIEW': return { bg: '#efe3c8', color: '#7d561c', border: '#efe3c8' };
      case 'REJECT': return { bg: '#f1e2da', color: '#963524', border: '#efe3c8' };
      case 'DO_NOT_USE': return { bg: '#f1e2da', color: '#b3412c', border: '#f2dad2' };
      default: return { bg: '#f5f2ea', color: '#857f73', border: '#e2ddd1' };
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
        <Shield size={24} style={{ display: 'inline', marginRight: '8px' }} />
        Bulk Carrier Screening
      </h1>
      <p style={{ color: '#857f73', marginBottom: '24px' }}>Screen up to 200 carriers at once. Paste DOT numbers or upload a CSV.</p>

      {/* Input Section */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: '#fbf9f4', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <textarea
            value={dotInput}
            onChange={e => setDotInput(e.target.value)}
            placeholder="Paste DOT numbers here (one per line, comma-separated, or space-separated)&#10;&#10;Example:&#10;100115&#10;4115094&#10;965044"
            style={{ width: '100%', height: '200px', padding: '12px', border: '1px solid #d3ccbc', borderRadius: '8px', fontSize: '14px', fontFamily: 'monospace', resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: '12px', marginTop: '12px', alignItems: 'center' }}>
            <button onClick={screen} disabled={loading}
              style={{ padding: '10px 24px', background: '#34312c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '15px', opacity: loading ? 0.6 : 1 }}>
              <Search size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
              {loading ? 'Screening...' : `Screen ${parseDots(dotInput).length} Carrier${parseDots(dotInput).length !== 1 ? 's' : ''}`}
            </button>
            {results.length > 0 && (
              <button onClick={exportCSV}
                style={{ padding: '10px 16px', background: '#fbf9f4', color: '#4a463f', border: '1px solid #d3ccbc', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}>
                <Download size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                Export CSV
              </button>
            )}
          </div>
          {error && <div style={{ color: '#b3412c', marginTop: '8px', fontSize: '14px' }}>{error}</div>}
        </div>

        <div style={{ background: '#fbf9f4', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>
            <Upload size={16} style={{ display: 'inline', marginRight: '6px' }} />
            Upload CSV
          </h3>
          <div
            onClick={() => fileRef.current?.click()}
            style={{ border: '2px dashed #d3ccbc', borderRadius: '8px', padding: '24px', textAlign: 'center', cursor: 'pointer', background: '#f5f2ea' }}
          >
            <FileText size={32} style={{ margin: '0 auto 8px', color: '#a39d90' }} />
            <div style={{ fontSize: '14px', color: '#857f73' }}>Click to upload CSV</div>
            <div style={{ fontSize: '12px', color: '#a39d90' }}>Any CSV with DOT numbers</div>
          </div>
          <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFileUpload} style={{ display: 'none' }} />

          {summary && (
            <div style={{ marginTop: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Results Summary</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div style={{ padding: '8px', background: '#e2e7d5', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4b5c3a' }}>{summary.approve}</div>
                  <div style={{ fontSize: '11px', color: '#4b5c3a' }}>Approve</div>
                </div>
                <div style={{ padding: '8px', background: '#efe3c8', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#7d561c' }}>{summary.review}</div>
                  <div style={{ fontSize: '11px', color: '#7d561c' }}>Review</div>
                </div>
                <div style={{ padding: '8px', background: '#f1e2da', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#963524' }}>{summary.reject}</div>
                  <div style={{ fontSize: '11px', color: '#963524' }}>Reject</div>
                </div>
                <div style={{ padding: '8px', background: '#f1e2da', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#b3412c' }}>{summary.doNotUse}</div>
                  <div style={{ fontSize: '11px', color: '#b3412c' }}>Do Not Use</div>
                </div>
              </div>
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#857f73', textAlign: 'center' }}>
                Avg Risk: {summary.avgRiskScore} | OOS: {summary.totalWithOOS} | Fatal: {summary.totalWithFatalCrashes}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Table */}
      {results.length > 0 && (
        <div style={{ background: '#fbf9f4', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f5f2ea' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e2ddd1' }}>Recommendation</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e2ddd1' }}>Carrier</th>
                <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e2ddd1' }}>Risk</th>
                <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e2ddd1' }}>Fleet</th>
                <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e2ddd1' }}>Rating</th>
                <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e2ddd1' }}>OOS%</th>
                <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e2ddd1' }}>Crashes</th>
                <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e2ddd1' }}>Ins.</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e2ddd1' }}>Risk Flags</th>
              </tr>
            </thead>
            <tbody>
              {results.map(r => {
                const rc = recColor(r.recommendation);
                return (
                  <tr key={r.dotNumber} style={{ borderBottom: '1px solid #efebe1' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: rc.bg, color: rc.color, border: `1px solid ${rc.border}` }}>
                        {r.recommendation === 'DO_NOT_USE' ? '🚫 DNU' : r.recommendation === 'REJECT' ? '❌ REJECT' : r.recommendation === 'REVIEW' ? '⚠️ REVIEW' : '✅ APPROVE'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 500 }}>{r.legalName}</div>
                      <div style={{ fontSize: '11px', color: '#a39d90' }}>DOT {r.dotNumber} · {r.state} · {r.powerUnits} units</div>
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 'bold', color: r.riskScore >= 50 ? '#b3412c' : r.riskScore >= 25 ? '#7d561c' : '#4b5c3a' }}>
                      {r.riskScore}
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'center' }}>{r.powerUnits}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 'bold', color: r.safetyRating === 'S' ? '#4b5c3a' : r.safetyRating === 'U' ? '#b3412c' : '#857f73' }}>
                      {r.safetyRating}
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'center', color: r.oosRate > 20 ? '#b3412c' : r.oosRate > 10 ? '#7d561c' : undefined }}>
                      {r.oosRate}%
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                      {r.fatalCrashes > 0 ? <span style={{ color: '#b3412c', fontWeight: 'bold' }}>💀{r.fatalCrashes}/{r.crashCount}</span> : r.crashCount}
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'center', color: r.insuranceStatus === 'Expired' ? '#b3412c' : undefined }}>
                      {r.insuranceStatus === 'Expired' ? '❌' : r.insuranceStatus === 'Active' ? '✅' : '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {r.riskFlags.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {r.riskFlags.map((f, i) => (
                            <span key={i} style={{ padding: '2px 6px', background: '#f1e2da', color: '#b3412c', borderRadius: '4px', fontSize: '11px' }}>{f}</span>
                          ))}
                        </div>
                      ) : <span style={{ color: '#4b5c3a', fontSize: '12px' }}>No flags</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BulkScreeningPage;
